import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import Layout from "../components/Layout";
import { logout } from "../common/api/auth";
import { AuthContext } from "../context/AuthContext";
import { ReportsContext } from "../context/ReportsContext";
import ModalSelectClient from "../components/user-management/ModalSelectClient";


function MyReports() {
  const { setIsLoggedIn } = useContext(AuthContext);
  const { codClients, reportClients = [], reportsStatus } = useContext(ReportsContext);
  const { codClient } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const [reportUrl, setReportUrl] = useState("");
  const [showClientModal, setShowClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState("");

  const reportOptions = useMemo(() => {
    if (Array.isArray(reportClients) && reportClients.length > 0) {
      return reportClients;
    }

    return codClients.map(client => ({
      label: `Informe ${client}`,
      value: client,
    }));
  }, [reportClients, codClients]);


  const fetchReport = useCallback(async (clientCode, signal) => {
    setStatus("loading");
    setError(null);
    setReportUrl("");

    const token = localStorage.getItem("token");

    try {
      const res = await fetch(`/reports-api/myreports/${clientCode}/index`, {
        headers: {
          Accept: "text/html",
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      if (!res.ok) {
        let message = "No se pudo cargar el informe.";

        if (res.status === 401) {
          message = "Tu sesión expiró. Inicia sesión nuevamente.";
          await logout();
          setIsLoggedIn(false);
          navigate("/login", { replace: true });
        } else if (res.status === 403) {
          message = "No autorizado.";
        } else if (res.status === 404) {
          message = "Este cliente no tiene informes disponibles.";
        }

        setError({ status: res.status, message });
        setStatus("error");
        return;
      }

      const html = await res.text();
      const assetMatch = html.match(/\/asset\/([^/]+)\//);

      if (!assetMatch) {
        console.error("No se encontró assetKey en el HTML del informe.");
        setError({
          status: "asset",
          message: "No se pudo cargar el informe.",
        });
        setStatus("error");
        return;
      }

      const assetKey = assetMatch[1];
      setReportUrl(`/reports-api/myreports/${clientCode}/index/${assetKey}`);
      setStatus("success");
    } catch (err) {
      if (err.name === "AbortError") {
        return;
      }
      console.error("Error cargando el informe:", err);
      setError({
        status: "network",
        message: "No se pudo conectar con el servidor de informes.",
      });
      setStatus("error");
    }
  }, [navigate, setIsLoggedIn]);

  useEffect(() => {
    if (!codClient) {
      // Aún no sabemos qué clientes hay
      if (reportsStatus !== "success") {
        setStatus("loading");
        return;
      }

      // Hay varios clientes → abrir selector
      if (reportOptions.length > 1) {
        setSelectedClient(reportOptions[0]?.value ?? "");
        setShowClientModal(true);
        setStatus("idle");
        return;
      }

      // Hay exactamente un cliente → navegar directo
      if (reportOptions.length === 1) {
        navigate(`/myreports/${reportOptions[0].value}`, { replace: true });
        return;
      }

      // No hay clientes
      setError({
        status: "empty",
        message: "No tiene informes disponibles.",
      });
      setStatus("empty");
      return;
    }

    // Hay codClient en la URL → intentar cargar informe
    const controller = new AbortController();
    fetchReport(codClient, controller.signal);

    return () => controller.abort();
  }, [codClient, reportOptions, reportsStatus, fetchReport, navigate]);


  const getDefaultClient = useCallback(() => {
    const candidates = codClients.length ? codClients : reportOptions.map(option => option.value);

    if (!candidates.length) {
      return "";
    }

    const preferredClient = codClient ?? "";
    const alternateClient = candidates.find(client => client !== preferredClient);

    if (preferredClient && alternateClient) {
      return alternateClient;
    }

    return candidates.includes(preferredClient) ? preferredClient : candidates[0];
  }, [codClient, codClients, reportOptions]);

  const handleOpenClientModal = () => {
    // No abrir si todavía no se han cargado los clientes/opciones
    if (reportsStatus !== "success" || reportOptions.length === 0) return;

    setSelectedClient(codClient ?? reportOptions[0]?.value ?? "");
    setShowClientModal(true);
  };



  const handleSelectClient = () => {
    if (!selectedClient) {
      return;
    }
    setShowClientModal(false);
    navigate(`/myreports/${selectedClient}`);
  };

  const handleCloseModal = () => {
    setShowClientModal(false);
    setSelectedClient("");
  };


  const isMissingReports = error?.status === "empty";
  const hasMultipleClients = reportOptions.length > 1;

  return (
    <Layout>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {showClientModal && (
          <ModalSelectClient
            clients={reportOptions}
            selectedClient={selectedClient}
            setSelectedClient={setSelectedClient}
            onSelect={handleSelectClient}
            onClose={handleCloseModal}
          />
        )}
        {status === "loading" && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress color="inherit" />
          </Box>
        )}

        {status === "error" && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" component="p">
              {error?.message}
            </Typography>
            {error?.status !== 404 && (
              <button
                className="boton_opciones"
                type="button"
                onClick={() => fetchReport(codClient)}
              >
                Reintentar
              </button>
            )}
          </Box>
        )}

        {status === "empty" && isMissingReports && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" component="p">
              {error?.message}
            </Typography>
          </Box>
        )}

        {status === "success" && (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <iframe
              title="Informe del usuario"
              src={reportUrl}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
            />
          </Box>
        )}
      </Box>
    </Layout>
  );
}

export default MyReports;
